import React, { useMemo, useRef, useState } from 'react';
import {
  Loader2, Sparkles, Download, Wand2, Palette, Check,
  ArrowLeft, ChevronLeft, ChevronRight, Upload, X, Image as ImageIcon,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';

import {
  generateAutoCarousel,
  updateAutoCarousel,
  downloadAutoCarousel,
  getMyBrand,
} from '../../lib/api';
import CarouselCardPreview from './CarouselCardPreview';
import { CarouselBrandingControls } from './CarouselBrandingControls';

// Font families that map to backend `font` param. Keep in sync with
// /app/backend/auto_carousel_service.py FONT_FAMILIES.
const FONT_OPTIONS = [
  { value: 'inter',    label: 'Inter',            hint: 'Modern · clean sans · default' },
  { value: 'opensans', label: 'Open Sans',        hint: 'Friendly · highly legible' },
  { value: 'playfair', label: 'Playfair Display', hint: 'Editorial · serif gravitas' },
];

// Quick CSS font-family preview for each option (used to style the in-dialog preview)
const FONT_FAMILY_CSS = {
  inter:    "'Inter', 'Helvetica Neue', Arial, sans-serif",
  opensans: "'Open Sans', 'Helvetica Neue', Arial, sans-serif",
  playfair: "'Playfair Display', 'Source Serif Pro', Georgia, serif",
};

// Curated swatches that map 1:1 to backend ACCENT_PRESETS in
// /app/backend/auto_carousel_service.py
const ACCENT_SWATCHES = [
  { id: 'ink',    label: 'Ink',    hex: '#0B1220' },
  { id: 'indigo', label: 'Indigo', hex: '#3949AB' },
  { id: 'teal',   label: 'Teal',   hex: '#0E7C7B' },
  { id: 'amber',  label: 'Amber',  hex: '#B45309' },
  { id: 'rose',   label: 'Rose',   hex: '#9F1239' },
  { id: 'olive',  label: 'Olive',  hex: '#4D7C0F' },
  { id: 'plum',   label: 'Plum',   hex: '#6B21A8' },
];

const TONES = [
  { value: 'professional', label: 'Professional', hint: 'Crisp, analytical, executive-grade' },
  { value: 'bold',         label: 'Bold',         hint: 'Confident, opinionated, punchy' },
  { value: 'minimalist',   label: 'Minimalist',   hint: 'Spare typography, lots of whitespace' },
  { value: 'premium',      label: 'Premium',      hint: 'Editorial gravitas, magazine feel' },
];

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function normalizeHex(value) {
  if (!value) return null;
  const v = value.trim();
  if (!HEX_RE.test(v)) return null;
  return v.startsWith('#') ? v : `#${v}`;
}

function pickReadableInk(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#0B1220';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#0B1220' : '#FFFFFF';
}

// ====================================================================
// Public component
// ====================================================================
export default function CarouselGeneratorDialog({
  open,
  onOpenChange,
  analysisId,
  sourceType,
  sourceIdx = 0,
  postPreview = '',
  initialTone = 'professional',
  initialDensity = 'concise',
  initialAccent = 'indigo',
  initialCardCount = 8,
  forceRegenerate = false,
  initialDesign = null,        // when provided, dialog opens directly in edit stage
  initialBrandLabel = '',
  onGenerated,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl p-0 overflow-hidden gap-0"
        data-testid="carousel-generator-dialog"
      >
        {open && (
          <DialogBody
            onOpenChange={onOpenChange}
            analysisId={analysisId}
            sourceType={sourceType}
            sourceIdx={sourceIdx}
            postPreview={postPreview}
            initialTone={initialTone}
            initialDensity={initialDensity}
            initialAccent={initialAccent}
            initialCardCount={initialCardCount}
            forceRegenerate={forceRegenerate}
            initialDesign={initialDesign}
            initialBrandLabel={initialBrandLabel}
            onGenerated={onGenerated}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ====================================================================
// Stateful body — split so opening the dialog gives a fresh state tree
// ====================================================================
function DialogBody({
  onOpenChange,
  analysisId,
  sourceType,
  sourceIdx,
  postPreview,
  initialTone,
  initialDensity,
  initialAccent,
  initialCardCount,
  forceRegenerate,
  initialDesign,
  initialBrandLabel,
  onGenerated,
}) {
  const hasInitialDesign = !!(initialDesign && (initialDesign.cards || initialDesign.slides || []).length);

  // Two stages: "configure" (pick options + generate) and "edit" (tweak cards & brand & download)
  const [stage, setStage] = useState(hasInitialDesign ? 'edit' : 'configure');

  // ----- configure stage state -----
  const [tone, setTone] = useState(initialTone);
  const [density, setDensity] = useState(initialDensity);
  const [platform, setPlatform] = useState('linkedin');
  const [cardCount, setCardCount] = useState(initialCardCount);
  const [accentId, setAccentId] = useState(initialAccent);
  const [customHex, setCustomHex] = useState('');
  const [font, setFont] = useState('inter');

  // ----- edit stage state (populated after generation) -----
  const [design, setDesign] = useState(() => {
    if (!hasInitialDesign) return null;
    const cards = initialDesign.cards || initialDesign.slides || [];
    return { ...initialDesign, cards };
  });
  const [selectedCardIdx, setSelectedCardIdx] = useState(0);
  const [brandLabel, setBrandLabel] = useState(initialBrandLabel || '');
  const [brandLogo, setBrandLogo] = useState(''); // data URL, empty = no logo
  const [busy, setBusy] = useState(false);

  // Phase 6 branding (deck-wide):
  const [logoPosition, setLogoPosition] = useState('header-left');
  const [bgDataUrl, setBgDataUrl] = useState(null);
  const [bgOpacity, setBgOpacity] = useState(0.18);

  // Auto-load the user's saved brand profile once on mount. We only fill in
  // values that are NOT already supplied by an `initialBrandLabel` or a
  // brand cached on the carousel itself.
  const brandLoaderRef = useRef(null);
  if (brandLoaderRef.current == null) {
    brandLoaderRef.current = getMyBrand()
      .then((data) => {
        if (!hasInitialDesign && !initialBrandLabel && data?.brand_label) {
          setBrandLabel(data.brand_label);
        }
        if (data?.brand_logo) {
          setBrandLogo((prev) => (prev ? prev : data.brand_logo));
        }
      })
      .catch(() => { /* non-fatal — brand profile is optional */ });
  }

  // Resolve which accent we send / paint
  const resolvedHex = useMemo(() => {
    const custom = normalizeHex(customHex);
    if (custom) return custom;
    const swatch = ACCENT_SWATCHES.find((s) => s.id === accentId);
    return swatch ? swatch.hex : '#3949AB';
  }, [customHex, accentId]);

  const accentPayload = useMemo(() => {
    const custom = normalizeHex(customHex);
    return custom || accentId;
  }, [customHex, accentId]);

  const customHexValid = customHex.length === 0 || normalizeHex(customHex) !== null;

  // -------- Generate (stage 1 -> stage 2) --------
  async function handleGenerate() {
    if (busy) return;
    setBusy(true);
    try {
      toast.info(forceRegenerate ? 'Re-rolling design…' : 'Designing carousel…');
      const out = await generateAutoCarousel(analysisId, sourceType, sourceIdx, {
        regenerate: forceRegenerate,
        tone,
        density,
        accentColor: accentPayload,
        cardCount,
        font,
        logoPosition,
        backgroundImage: bgDataUrl || undefined,
        backgroundOpacity: bgOpacity,
      });
      const d = out?.design || {};
      const cards = d.cards || d.slides || [];
      if (!cards.length) throw new Error('Generator returned no cards');
      setDesign({ ...d, cards });
      // Only overwrite the loaded user brand label if backend gave a stronger one
      if (out?.brand_label) setBrandLabel((prev) => prev || out.brand_label);
      setSelectedCardIdx(0);
      setStage('edit');
      if (onGenerated) onGenerated(cards.length);
      toast.success(out?.cached ? 'Loaded cached design — edit & download' : `Designed ${cards.length}-card carousel`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not generate carousel');
    } finally {
      setBusy(false);
    }
  }

  // -------- Save & Download (stage 2) --------
  async function handleSaveAndDownload() {
    if (busy) return;
    setBusy(true);
    try {
      toast.info('Saving edits…');
      await updateAutoCarousel(analysisId, sourceType, sourceIdx, {
        design,
        brandLabel,
        brandLogo: brandLogo || '',
        accentColor: accentPayload,
        font,
        logoPosition,
        backgroundImage: bgDataUrl === null ? '' : (bgDataUrl || ''),
        backgroundOpacity: bgOpacity,
      });
      toast.info('Rendering PDF…');
      await downloadAutoCarousel(analysisId, sourceType, sourceIdx);
      toast.success('Carousel PDF downloaded');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  }

  // -------- Card updater --------
  function updateSelectedCard(patch) {
    if (!design) return;
    const next = { ...design, cards: design.cards.slice() };
    next.cards[selectedCardIdx] = { ...next.cards[selectedCardIdx], ...patch };
    setDesign(next);
  }

  function updateDesignField(key, value) {
    if (!design) return;
    setDesign({ ...design, [key]: value });
  }

  return stage === 'configure' ? (
    <ConfigureStage
      tone={tone} setTone={setTone}
      density={density} setDensity={setDensity}
      platform={platform} setPlatform={setPlatform}
      cardCount={cardCount} setCardCount={setCardCount}
      accentId={accentId} setAccentId={setAccentId}
      customHex={customHex} setCustomHex={setCustomHex}
      customHexValid={customHexValid}
      resolvedHex={resolvedHex}
      font={font} setFont={setFont}
      postPreview={postPreview}
      busy={busy}
      forceRegenerate={forceRegenerate}
      onCancel={() => onOpenChange(false)}
      onGenerate={handleGenerate}
    />
  ) : (
    <EditStage
      design={design}
      selectedCardIdx={selectedCardIdx}
      setSelectedCardIdx={setSelectedCardIdx}
      updateSelectedCard={updateSelectedCard}
      updateDesignField={updateDesignField}
      brandLabel={brandLabel}
      setBrandLabel={setBrandLabel}
      brandLogo={brandLogo}
      setBrandLogo={setBrandLogo}
      logoPosition={logoPosition}
      setLogoPosition={setLogoPosition}
      bgDataUrl={bgDataUrl}
      setBgDataUrl={setBgDataUrl}
      bgOpacity={bgOpacity}
      setBgOpacity={setBgOpacity}
      accentId={accentId}
      setAccentId={setAccentId}
      customHex={customHex}
      setCustomHex={setCustomHex}
      customHexValid={customHexValid}
      resolvedHex={resolvedHex}
      font={font} setFont={setFont}
      busy={busy}
      onBack={() => setStage('configure')}
      onCancel={() => onOpenChange(false)}
      onDownload={handleSaveAndDownload}
    />
  );
}

// ====================================================================
// Stage 1 — Configure
// ====================================================================
function ConfigureStage({
  tone, setTone,
  density, setDensity,
  platform, setPlatform,
  cardCount, setCardCount,
  accentId, setAccentId,
  customHex, setCustomHex,
  customHexValid,
  resolvedHex,
  font, setFont,
  postPreview,
  busy,
  forceRegenerate,
  onCancel,
  onGenerate,
}) {
  const inkOnAccent = pickReadableInk(resolvedHex);
  const snippetForPreview = useMemo(() => {
    const fallback = 'Your carousel headline will appear here.';
    if (!postPreview) return fallback;
    const cleaned = String(postPreview).replace(/\s+/g, ' ').trim();
    return cleaned.length > 180 ? `${cleaned.slice(0, 180)}…` : cleaned;
  }, [postPreview]);

  return (
    <>
      <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div
            className="h-8 w-8 rounded-lg grid place-items-center"
            style={{ backgroundColor: resolvedHex, color: inkOnAccent }}
          >
            <Wand2 size={16} />
          </div>
          <div>
            <DialogTitle className="text-base font-semibold tracking-tight">
              Design carousel · Step 1 of 2
            </DialogTitle>
            <DialogDescription className="text-xs">
              Pick a style and we will draft cards. You can edit text and branding in the next step.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="grid md:grid-cols-[1fr,420px] gap-0">
        {/* CONTROLS */}
        <div className="p-6 space-y-6 max-h-[68vh] overflow-y-auto">
          <ToneControl tone={tone} setTone={setTone} />
          <PlatformControl platform={platform} setPlatform={setPlatform} />
          <CardCountControl cardCount={cardCount} setCardCount={setCardCount} />
          <DensityControl density={density} setDensity={setDensity} />
          <FontControl font={font} setFont={setFont} />
          <AccentControl
            accentId={accentId} setAccentId={setAccentId}
            customHex={customHex} setCustomHex={setCustomHex}
            customHexValid={customHexValid}
          />
        </div>

        {/* PREVIEW — 3-slide mini-carousel mirroring the HTML/Chromium aesthetic */}
        <div className="bg-muted/40 border-l border-border p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium tracking-wide text-muted-foreground">
              STYLE PREVIEW
            </Label>
            <Badge variant="outline" className="text-[10px] font-mono">
              Sample · 4:5
            </Badge>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <StylePreviewMiniDeck
              accent={resolvedHex}
              font={font}
              tone={tone}
              snippet={snippetForPreview}
            />
          </div>
          <p className="text-[11px] text-muted-foreground text-center px-2">
            A faithful, editable preview of the actual cards (matching the PDF) is on the next step.
          </p>
        </div>
      </div>

      <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground hidden sm:block">
          Next step: edit text, upload logo, then download.
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={busy}
            data-testid="carousel-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onGenerate}
            disabled={busy || !customHexValid}
            className="gap-1.5"
            data-testid="carousel-generate-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy
              ? 'Designing…'
              : forceRegenerate
              ? 'Re-roll & edit'
              : 'Generate & edit'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

// Pretend cover card to demo the chosen tone before generation
function mockPreviewCard(tone, snippet) {
  const map = {
    bold:       { kicker: 'THE TAKE',  headline: 'Stop optimizing for noise.' },
    minimalist: { kicker: 'NOTE',      headline: 'Less, but louder.' },
    premium:    { kicker: 'CHAPTER 01',headline: 'The quiet edge of expertise.' },
    professional: { kicker: 'INSIGHT', headline: 'How operators win the long game.' },
  };
  const c = map[tone] || map.professional;
  return {
    layout: 'insight',
    kicker: c.kicker,
    headline: c.headline,
    body: snippet,
    highlights: [],
  };
}

// ====================================================================
// StylePreviewMiniDeck
// 3-slide mini-carousel matching the HTML/Chromium template aesthetic
// (radial-gradient cover, accent-tint insight, accent-pill CTA).
// Pure CSS — not Playwright — but visually aligned with base.html tokens.
// ====================================================================
const FONT_FAMILIES_PREVIEW = {
  inter:    "'Inter', 'Helvetica Neue', Arial, sans-serif",
  opensans: "'Open Sans', 'Helvetica Neue', Arial, sans-serif",
  playfair: "'Playfair Display', 'Source Serif Pro', Georgia, serif",
};

// Convert hex to "darker companion" (mirrors accent_soft in base.html palettes).
function darkenHex(hex, amount = 0.4) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#1F2937';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = Math.max(0, Math.min(1, 1 - amount));
  const toHex = (v) => Math.round(v * f).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Soft tint (mirrors accent_tint in base.html palettes).
function tintHex(hex, alpha = 0.18) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return 'rgba(99,102,241,0.18)';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function StylePreviewMiniDeck({ accent, font, tone, snippet }) {
  const accentSoft = darkenHex(accent, 0.55);
  const tint = tintHex(accent, 0.18);
  const fontStack = FONT_FAMILIES_PREVIEW[font] || FONT_FAMILIES_PREVIEW.inter;

  const toneCopy = {
    bold:         { cover: 'Stop optimizing for noise.',          kicker: 'THE TAKE',   insight: 'Operators win where strategists posture.', cta: 'Take the contrarian path.' },
    minimalist:   { cover: 'Less, but louder.',                   kicker: 'NOTE',       insight: 'Spare typography. Long memory.',           cta: 'Say less. Mean more.' },
    premium:      { cover: 'The quiet edge of expertise.',        kicker: 'CHAPTER 01', insight: 'Compounding clarity over years.',          cta: 'Build the long edge.' },
    professional: { cover: 'How operators win the long game.',    kicker: 'INSIGHT',    insight: 'Process beats inspiration.',               cta: 'Start auditing today.' },
  };
  const t = toneCopy[tone] || toneCopy.professional;

  // Each card renders ~180px wide; with overlap they fan out like a stack.
  const cardW = 180;
  const cardH = 225; // 4:5

  const cardBase = {
    width: cardW,
    height: cardH,
    borderRadius: 14,
    boxShadow: '0 18px 40px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.08)',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
    fontFamily: fontStack,
  };

  return (
    <div
      className="relative"
      style={{ height: cardH + 40, width: '100%', maxWidth: 360 }}
      data-testid="carousel-style-preview-mini-deck"
    >
      {/* Fan layout: insight in back-left, cover in center-front, cta in back-right */}
      <div
        style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {/* INSIGHT (back-left) */}
        <div
          style={{
            ...cardBase,
            background: '#F8FAFC',
            transform: 'translateX(-58px) rotate(-7deg)',
            zIndex: 1,
            border: '1px solid #E5E7EB',
          }}
        >
          {/* Dotted grid background */}
          <div
            style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(circle, #E5E7EB 0.8px, transparent 0.8px)',
              backgroundSize: '10px 10px',
              opacity: 0.6,
            }}
          />
          {/* Accent tint glow (top-right) */}
          <div
            style={{
              position: 'absolute', inset: 0,
              background: `radial-gradient(ellipse at 90% 0%, ${tint} 0%, transparent 60%)`,
            }}
          />
          {/* Top chrome */}
          <div style={{ position: 'absolute', top: 12, left: 14, right: 14, display: 'flex', alignItems: 'center', gap: 5, zIndex: 2 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
            <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.14em', color: '#1F2937' }}>
              EXECPRESENCEAI
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 5.5, fontWeight: 600, letterSpacing: '0.12em', color: '#9CA3AF' }}>
              2/{8}
            </span>
          </div>
          <div style={{ position: 'absolute', top: 28, left: 14, right: 14, height: 1, background: '#E5E7EB' }} />
          {/* Body */}
          <div style={{ position: 'absolute', top: 42, left: 14, right: 14, bottom: 28, display: 'flex', flexDirection: 'column', zIndex: 2 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 6.5, fontWeight: 800, letterSpacing: '0.18em', color: accent, textTransform: 'uppercase' }}>
              {t.kicker}
              <span style={{ width: 14, height: 1.5, background: accent, borderRadius: 2 }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.15, fontWeight: 800, color: '#0B0F1F', letterSpacing: '-0.01em' }}>
              {t.insight}
            </div>
            {/* Flourish */}
            <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
              <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${accent} 0%, transparent 100%)`, opacity: 0.35 }} />
              <span
                style={{
                  width: 18, height: 18, borderRadius: 5,
                  background: tint, color: accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* COVER (center, on top) - radial gradient accent-to-ink */}
        <div
          style={{
            ...cardBase,
            background: `radial-gradient(ellipse at 80% 0%, ${accentSoft} 0%, #0B0F1F 70%)`,
            transform: 'translateY(-6px) rotate(0deg)',
            zIndex: 3,
          }}
        >
          {/* Top chrome */}
          <div style={{ position: 'absolute', top: 12, left: 14, right: 14, display: 'flex', alignItems: 'center', gap: 5, color: '#fff', zIndex: 2 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
            <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.9)' }}>
              EXECPRESENCEAI
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 5.5, fontWeight: 600, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.5)' }}>
              1/{8}
            </span>
          </div>
          <div style={{ position: 'absolute', top: 28, left: 14, right: 14, height: 1, background: 'rgba(255,255,255,0.12)' }} />
          {/* Cover body */}
          <div style={{ position: 'absolute', top: 50, left: 14, right: 14, bottom: 38, display: 'flex', flexDirection: 'column', zIndex: 2 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 6.5, fontWeight: 800, letterSpacing: '0.2em', color: accent, textTransform: 'uppercase' }}>
              COVER
              <span style={{ width: 14, height: 1.5, background: accent, borderRadius: 2 }} />
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 17, lineHeight: 1.04,
                fontWeight: 800, color: '#fff',
                letterSpacing: '-0.02em',
              }}
            >
              {t.cover}
            </div>
            <div
              style={{
                marginTop: 'auto',
                fontSize: 8, color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.4,
              }}
            >
              {(snippet || '').slice(0, 90)}
            </div>
          </div>
          {/* Bottom chrome */}
          <div style={{ position: 'absolute', bottom: 12, left: 14, right: 14, height: 1, background: 'rgba(255,255,255,0.12)' }} />
          <div style={{ position: 'absolute', bottom: 2, left: 14, right: 14, display: 'flex', justifyContent: 'space-between', fontSize: 5.5, fontWeight: 600, letterSpacing: '0.18em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>
            <span>SWIPE →</span>
            <span style={{ color: '#fff', fontWeight: 800 }}>1 / 8</span>
          </div>
        </div>

        {/* CTA (back-right) - dark gradient with accent pill */}
        <div
          style={{
            ...cardBase,
            background: `radial-gradient(ellipse at 20% 100%, ${accentSoft} 0%, #0B0F1F 70%)`,
            transform: 'translateX(58px) rotate(7deg)',
            zIndex: 1,
          }}
        >
          {/* Top chrome */}
          <div style={{ position: 'absolute', top: 12, left: 14, right: 14, display: 'flex', alignItems: 'center', gap: 5, zIndex: 2 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
            <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.85)' }}>
              EXECPRESENCEAI
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 5.5, fontWeight: 600, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.5)' }}>
              8/{8}
            </span>
          </div>
          <div style={{ position: 'absolute', top: 28, left: 14, right: 14, height: 1, background: 'rgba(255,255,255,0.12)' }} />
          <div style={{ position: 'absolute', top: 44, left: 14, right: 14, bottom: 28, display: 'flex', flexDirection: 'column', zIndex: 2 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 6.5, fontWeight: 800, letterSpacing: '0.2em', color: accent, textTransform: 'uppercase' }}>
              YOUR MOVE
              <span style={{ width: 14, height: 1.5, background: accent, borderRadius: 2 }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.1, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>
              {t.cta}
            </div>
            <div
              style={{
                marginTop: 'auto',
                alignSelf: 'flex-start',
                background: accent,
                color: '#fff',
                fontSize: 7, fontWeight: 800,
                letterSpacing: '0.1em',
                padding: '6px 12px',
                borderRadius: 999,
                textTransform: 'uppercase',
              }}
            >
              Get started →
            </div>
          </div>
        </div>
      </div>

      {/* Label strip */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
          Insight
        </span>
        <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-foreground font-semibold">
          Cover
        </span>
        <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
          CTA
        </span>
      </div>
    </div>
  );
}

// ====================================================================
// Stage 2 — Edit
// ====================================================================
function EditStage({
  design,
  selectedCardIdx,
  setSelectedCardIdx,
  updateSelectedCard,
  updateDesignField,
  brandLabel, setBrandLabel,
  brandLogo,  setBrandLogo,
  logoPosition, setLogoPosition,
  bgDataUrl, setBgDataUrl,
  bgOpacity, setBgOpacity,
  accentId, setAccentId,
  customHex, setCustomHex,
  customHexValid,
  resolvedHex,
  font, setFont,
  busy,
  onBack,
  onCancel,
  onDownload,
}) {
  const cards = design?.cards || [];
  const total = cards.length;
  const card = cards[selectedCardIdx] || cards[0];
  const inkOnAccent = pickReadableInk(resolvedHex);

  function goPrev() { setSelectedCardIdx(Math.max(0, selectedCardIdx - 1)); }
  function goNext() { setSelectedCardIdx(Math.min(total - 1, selectedCardIdx + 1)); }

  return (
    <>
      <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div
            className="h-8 w-8 rounded-lg grid place-items-center"
            style={{ backgroundColor: resolvedHex, color: inkOnAccent }}
          >
            <Wand2 size={16} />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-base font-semibold tracking-tight">
              Edit & brand · Step 2 of 2
            </DialogTitle>
            <DialogDescription className="text-xs">
              Tweak any card and add your brand. The preview matches the PDF exactly.
            </DialogDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            disabled={busy}
            className="gap-1.5"
            data-testid="carousel-back-btn"
          >
            <ArrowLeft size={14} /> Style
          </Button>
        </div>
      </DialogHeader>

      <div className="grid md:grid-cols-[420px,1fr] gap-0">
        {/* EDITOR PANEL (left) */}
        <div className="p-5 border-r border-border max-h-[72vh] overflow-y-auto space-y-5">
          {/* Brand block */}
          <section className="space-y-3">
            <Label className="text-[11px] font-medium tracking-wide text-muted-foreground">
              BRAND
            </Label>
            <div className="space-y-2">
              <Label htmlFor="brand-label-input" className="text-xs">Brand name</Label>
              <Input
                id="brand-label-input"
                value={brandLabel}
                onChange={(e) => setBrandLabel(e.target.value.slice(0, 60))}
                placeholder="Your name or company"
                data-testid="carousel-brand-label-input"
              />
            </div>
            <CarouselBrandingControls
              logoDataUrl={brandLogo || null}
              onLogoChange={(v) => setBrandLogo(v || '')}
              logoPosition={logoPosition}
              onLogoPositionChange={setLogoPosition}
              bgDataUrl={bgDataUrl}
              onBgChange={setBgDataUrl}
              bgOpacity={bgOpacity}
              onBgOpacityChange={setBgOpacity}
              testIdPrefix="carousel-dialog-branding"
            />
            <AccentControl
              accentId={accentId} setAccentId={setAccentId}
              customHex={customHex} setCustomHex={setCustomHex}
              customHexValid={customHexValid}
              compact
            />
            <FontControl font={font} setFont={setFont} />
          </section>

          {/* Deck-level fields */}
          <section className="space-y-2 pt-1">
            <Label className="text-[11px] font-medium tracking-wide text-muted-foreground">
              DECK
            </Label>
            <div className="space-y-1.5">
              <Label htmlFor="deck-title-input" className="text-[11px]">Deck title (header eyebrow)</Label>
              <Input
                id="deck-title-input"
                value={design?.title || ''}
                onChange={(e) => updateDesignField('title', e.target.value.slice(0, 60))}
                data-testid="carousel-deck-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deck-sub-input" className="text-[11px]">Deck subtitle (cover only)</Label>
              <Input
                id="deck-sub-input"
                value={design?.subtitle || ''}
                onChange={(e) => updateDesignField('subtitle', e.target.value.slice(0, 200))}
                data-testid="carousel-deck-subtitle-input"
              />
            </div>
          </section>

          {/* Per-card editor */}
          <section className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-medium tracking-wide text-muted-foreground">
                CARD {selectedCardIdx + 1} OF {total} · {(card?.layout || 'insight').toUpperCase()}
              </Label>
            </div>
            <CardEditor card={card} update={updateSelectedCard} />
          </section>
        </div>

        {/* PREVIEW + THUMBNAILS (right) */}
        <div className="bg-muted/30 p-5 max-h-[72vh] overflow-y-auto flex flex-col gap-4">
          {/* Active card preview */}
          <div className="flex items-center justify-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={goPrev}
              disabled={selectedCardIdx === 0 || busy}
              className="h-8 w-8"
              data-testid="carousel-prev-card"
            >
              <ChevronLeft size={16} />
            </Button>
            <CarouselCardPreview
              card={card}
              idx={selectedCardIdx + 1}
              total={total}
              deckTitle={design?.title || ''}
              deckSubtitle={design?.subtitle || ''}
              brandLabel={brandLabel}
              brandLogo={brandLogo}
              accent={resolvedHex}
              font={font}
              width={360}
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={goNext}
              disabled={selectedCardIdx === total - 1 || busy}
              className="h-8 w-8"
              data-testid="carousel-next-card"
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Thumbnail row */}
          <div>
            <Label className="text-[10px] font-medium tracking-wider text-muted-foreground mb-2 block">
              ALL CARDS
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {cards.map((c, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setSelectedCardIdx(i)}
                  className={[
                    'rounded-md overflow-hidden border-2 transition-all bg-white',
                    i === selectedCardIdx
                      ? 'border-primary shadow-md ring-2 ring-primary/20'
                      : 'border-transparent hover:border-border opacity-70 hover:opacity-100',
                  ].join(' ')}
                  data-testid={`carousel-thumb-${i}`}
                  aria-label={`Card ${i + 1}`}
                >
                  <CarouselCardPreview
                    card={c}
                    idx={i + 1}
                    total={total}
                    deckTitle={design?.title || ''}
                    deckSubtitle={design?.subtitle || ''}
                    brandLabel={brandLabel}
                    brandLogo={brandLogo}
                    accent={resolvedHex}
                    font={font}
                    width={120}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground hidden sm:block">
          Your edits are saved with the deck — re-downloading will use them.
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={busy}
            data-testid="carousel-close-btn"
          >
            Close
          </Button>
          <Button
            onClick={onDownload}
            disabled={busy || !customHexValid}
            className="gap-1.5"
            data-testid="carousel-download-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {busy ? 'Saving & rendering…' : 'Save & download PDF'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

// ====================================================================
// Layout-aware per-card editor
// ====================================================================
function CardEditor({ card, update }) {
  if (!card) return null;
  const layout = card.layout || 'insight';

  return (
    <div className="space-y-3">
      <FieldRow label="Layout">
        <Select value={layout} onValueChange={(v) => update({ layout: v })}>
          <SelectTrigger data-testid="carousel-card-layout-select" className="h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['cover','insight','bullets','quote','stat','two_col','cta'].map((l) => (
              <SelectItem key={l} value={l} className="text-xs">{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      {layout !== 'quote' && (
        <FieldRow label="Kicker">
          <Input
            value={card.kicker || ''}
            onChange={(e) => update({ kicker: e.target.value.slice(0, 40) })}
            placeholder="THE TAKE"
            className="h-9 text-xs"
            data-testid="carousel-card-kicker"
          />
        </FieldRow>
      )}

      {layout !== 'quote' && layout !== 'stat' && (
        <FieldRow label="Headline">
          <Textarea
            value={card.headline || ''}
            onChange={(e) => update({ headline: e.target.value.slice(0, 200) })}
            rows={2}
            placeholder="Your headline"
            className="text-xs min-h-[60px]"
            data-testid="carousel-card-headline"
          />
        </FieldRow>
      )}

      {(layout === 'insight' || layout === 'cta' || layout === 'stat') && (
        <FieldRow label="Body">
          <Textarea
            value={card.body || ''}
            onChange={(e) => update({ body: e.target.value.slice(0, 500) })}
            rows={4}
            placeholder="Supporting copy"
            className="text-xs min-h-[80px]"
            data-testid="carousel-card-body"
          />
        </FieldRow>
      )}

      {layout === 'bullets' && (
        <BulletsEditor
          bullets={card.bullets || []}
          highlights={card.bullet_highlights || []}
          onChange={(bullets, bullet_highlights) =>
            update({ bullets, bullet_highlights })
          }
        />
      )}

      {layout === 'quote' && (
        <>
          <FieldRow label="Quote">
            <Textarea
              value={card.quote || ''}
              onChange={(e) => update({ quote: e.target.value.slice(0, 300) })}
              rows={3}
              className="text-xs min-h-[70px]"
              data-testid="carousel-card-quote"
            />
          </FieldRow>
          <FieldRow label="Attribution">
            <Input
              value={card.attribution || ''}
              onChange={(e) => update({ attribution: e.target.value.slice(0, 80) })}
              className="h-9 text-xs"
              data-testid="carousel-card-attribution"
            />
          </FieldRow>
        </>
      )}

      {layout === 'stat' && (
        <>
          <FieldRow label="Stat">
            <Input
              value={card.stat || ''}
              onChange={(e) => update({ stat: e.target.value.slice(0, 12) })}
              placeholder="30%"
              className="h-9 text-base font-semibold"
              data-testid="carousel-card-stat"
            />
          </FieldRow>
          <FieldRow label="Stat label">
            <Input
              value={card.stat_label || ''}
              onChange={(e) => update({ stat_label: e.target.value.slice(0, 120) })}
              className="h-9 text-xs"
              data-testid="carousel-card-stat-label"
            />
          </FieldRow>
        </>
      )}

      {layout === 'two_col' && (
        <>
          <FieldRow label="Left heading">
            <Input
              value={card.two_col_left || ''}
              onChange={(e) => update({ two_col_left: e.target.value.slice(0, 40) })}
              className="h-9 text-xs"
              data-testid="carousel-card-twocol-left-h"
            />
          </FieldRow>
          <FieldRow label="Left body">
            <Textarea
              value={card.two_col_left_body || ''}
              onChange={(e) => update({ two_col_left_body: e.target.value.slice(0, 240) })}
              rows={3}
              className="text-xs"
              data-testid="carousel-card-twocol-left-b"
            />
          </FieldRow>
          <FieldRow label="Right heading">
            <Input
              value={card.two_col_right || ''}
              onChange={(e) => update({ two_col_right: e.target.value.slice(0, 40) })}
              className="h-9 text-xs"
              data-testid="carousel-card-twocol-right-h"
            />
          </FieldRow>
          <FieldRow label="Right body">
            <Textarea
              value={card.two_col_right_body || ''}
              onChange={(e) => update({ two_col_right_body: e.target.value.slice(0, 240) })}
              rows={3}
              className="text-xs"
              data-testid="carousel-card-twocol-right-b"
            />
          </FieldRow>
        </>
      )}

      {layout === 'cta' && (
        <FieldRow label="CTA button text">
          <Input
            value={card.cta || ''}
            onChange={(e) => update({ cta: e.target.value.slice(0, 40) })}
            placeholder="JOIN THE CONVERSATION"
            className="h-9 text-xs"
            data-testid="carousel-card-cta"
          />
        </FieldRow>
      )}

      {layout !== 'quote' && layout !== 'stat' && layout !== 'two_col' && (
        <FieldRow label="Highlight phrases (comma separated)">
          <Input
            value={(card.highlights || []).join(', ')}
            onChange={(e) =>
              update({
                highlights: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 5),
              })
            }
            placeholder="e.g. quiet edge, long game"
            className="h-9 text-xs"
            data-testid="carousel-card-highlights"
          />
        </FieldRow>
      )}
    </div>
  );
}

function BulletsEditor({ bullets, highlights, onChange }) {
  const list = bullets.length ? bullets : [''];
  const setBullet = (i, value) => {
    const next = list.slice();
    next[i] = value;
    onChange(next, highlights);
  };
  const setHigh = (i, value) => {
    const next = highlights.slice();
    next[i] = value;
    onChange(list, next);
  };
  const addBullet = () => onChange([...list, ''], highlights);
  const removeBullet = (i) => {
    const nextB = list.slice(); nextB.splice(i, 1);
    const nextH = highlights.slice(); nextH.splice(i, 1);
    onChange(nextB, nextH);
  };

  return (
    <div className="space-y-2">
      <Label className="text-[11px] tracking-wide text-muted-foreground">Bullets</Label>
      {list.map((b, i) => (
        <div key={i} className="space-y-1">
          <div className="flex gap-1.5 items-start">
            <span className="text-[10px] text-muted-foreground pt-2 w-3 font-mono">{i + 1}</span>
            <Input
              value={b}
              onChange={(e) => setBullet(i, e.target.value.slice(0, 200))}
              className="h-9 text-xs flex-1"
              data-testid={`carousel-card-bullet-${i}`}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-7"
              onClick={() => removeBullet(i)}
              disabled={list.length === 1}
              data-testid={`carousel-card-bullet-remove-${i}`}
            >
              <X size={12} />
            </Button>
          </div>
          <div className="flex gap-1.5 items-center pl-4">
            <span className="text-[9px] text-muted-foreground w-12 uppercase tracking-wider">Highlight</span>
            <Input
              value={highlights[i] || ''}
              onChange={(e) => setHigh(i, e.target.value.slice(0, 30))}
              placeholder="2-4 word phrase to accent"
              className="h-7 text-[11px] flex-1"
              data-testid={`carousel-card-bullet-highlight-${i}`}
            />
          </div>
        </div>
      ))}
      {list.length < 5 && (
        <Button
          variant="outline"
          size="sm"
          onClick={addBullet}
          className="w-full h-8 text-xs"
          data-testid="carousel-card-bullet-add"
        >
          + Add bullet
        </Button>
      )}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ====================================================================
// Small control widgets reused by both stages
// ====================================================================
function ToneControl({ tone, setTone }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium tracking-wide text-muted-foreground">
        TONE
      </Label>
      <Select value={tone} onValueChange={setTone}>
        <SelectTrigger data-testid="carousel-tone-select" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TONES.map((t) => (
            <SelectItem
              key={t.value}
              value={t.value}
              data-testid={`carousel-tone-option-${t.value}`}
            >
              <div className="flex flex-col">
                <span className="text-sm">{t.label}</span>
                <span className="text-[11px] text-muted-foreground">{t.hint}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PlatformControl({ platform, setPlatform }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium tracking-wide text-muted-foreground">
        PLATFORM
      </Label>
      <ToggleGroup
        type="single"
        value={platform}
        onValueChange={(v) => v && setPlatform(v)}
        className="justify-start gap-2"
      >
        <ToggleGroupItem
          value="linkedin"
          className="px-3 py-1.5 text-xs h-auto rounded-md border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          data-testid="carousel-platform-linkedin"
        >
          LinkedIn · 4:5
        </ToggleGroupItem>
        <ToggleGroupItem
          value="instagram"
          className="px-3 py-1.5 text-xs h-auto rounded-md border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          data-testid="carousel-platform-instagram"
        >
          Instagram · 4:5
        </ToggleGroupItem>
      </ToggleGroup>
      <p className="text-[11px] text-muted-foreground">
        Both export at 4:5 portrait — the optimal native ratio for both feeds.
      </p>
    </div>
  );
}

function CardCountControl({ cardCount, setCardCount }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium tracking-wide text-muted-foreground">CARDS</Label>
        <Badge
          variant="secondary"
          className="font-mono text-[11px]"
          data-testid="carousel-card-count-value"
        >
          {cardCount}
        </Badge>
      </div>
      <Slider
        value={[cardCount]}
        min={6}
        max={10}
        step={1}
        onValueChange={(v) => setCardCount(v[0])}
        data-testid="carousel-card-count-slider"
      />
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground/70">
        <span>6</span><span>7</span><span>8</span><span>9</span><span>10</span>
      </div>
    </div>
  );
}

function DensityControl({ density, setDensity }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium tracking-wide text-muted-foreground">
        CONTENT DENSITY
      </Label>
      <ToggleGroup
        type="single"
        value={density}
        onValueChange={(v) => v && setDensity(v)}
        className="justify-start gap-2"
      >
        <ToggleGroupItem
          value="concise"
          className="px-3 py-1.5 text-xs h-auto rounded-md border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          data-testid="carousel-density-concise"
        >
          Concise
        </ToggleGroupItem>
        <ToggleGroupItem
          value="medium"
          className="px-3 py-1.5 text-xs h-auto rounded-md border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          data-testid="carousel-density-medium"
        >
          Balanced
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

function FontControl({ font, setFont }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium tracking-wide text-muted-foreground">
        FONT
      </Label>
      <Select value={font} onValueChange={setFont}>
        <SelectTrigger data-testid="carousel-font-select" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FONT_OPTIONS.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              data-testid={`carousel-font-option-${opt.value}`}
            >
              <div className="flex flex-col">
                <span
                  className="text-sm"
                  style={{ fontFamily: FONT_FAMILY_CSS[opt.value] }}
                >
                  {opt.label}
                </span>
                <span className="text-[11px] text-muted-foreground">{opt.hint}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p
        className="text-[11px] text-muted-foreground"
        style={{ fontFamily: FONT_FAMILY_CSS[font] }}
      >
        Preview: The quick brown fox jumps over the lazy dog — “curly quotes” work.
      </p>
    </div>
  );
}

function AccentControl({
  accentId, setAccentId,
  customHex, setCustomHex,
  customHexValid,
  compact = false,
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Palette size={12} /> ACCENT COLOR
      </Label>
      <div className={`grid ${compact ? 'grid-cols-7' : 'grid-cols-7'} gap-2`}>
        {ACCENT_SWATCHES.map((s) => {
          const selected = !customHex && accentId === s.id;
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => { setAccentId(s.id); setCustomHex(''); }}
              title={s.label}
              data-testid={`carousel-accent-${s.id}`}
              className={[
                'group relative aspect-square rounded-md border transition-all',
                'hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                selected ? 'border-foreground shadow-sm' : 'border-border',
              ].join(' ')}
              style={{ backgroundColor: s.hex }}
            >
              {selected && (
                <Check
                  size={14}
                  className="absolute inset-0 m-auto"
                  style={{ color: pickReadableInk(s.hex) }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="pt-1">
        <Label
          htmlFor="carousel-custom-hex"
          className="text-[10px] tracking-wide text-muted-foreground"
        >
          Custom hex
        </Label>
        <div className="flex gap-2 items-center mt-1">
          <div
            className="h-9 w-9 rounded-md border border-border shrink-0"
            style={{ backgroundColor: customHexValid && customHex ? normalizeHex(customHex) : 'transparent' }}
          />
          <Input
            id="carousel-custom-hex"
            value={customHex}
            onChange={(e) => setCustomHex(e.target.value)}
            placeholder="#0B5FAD"
            className={customHexValid ? '' : 'border-destructive'}
            data-testid="carousel-custom-hex-input"
          />
        </div>
        {!customHexValid && (
          <p className="text-[11px] text-destructive mt-1">
            Use a 3 or 6-digit hex value (e.g. #0B5FAD).
          </p>
        )}
      </div>
    </div>
  );
}
